package org.bsvac.bsvac_kmp.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import org.bsvac.bsvac_kmp.MainUiState
import org.bsvac.bsvac_kmp.data.BsvacTextStyle
import org.bsvac.bsvac_kmp.data.models.ContentSectionId

internal class SubScreenDisplay (
    val sectionId: ContentSectionId,
    val display: @Composable (BoxScope) -> Unit,
)

@Composable
fun TopRow(
    scope: ColumnScope,
    mainUiState: MainUiState,
    sectionIds: List<ContentSectionId>,
    navigationClicked: (ContentSectionId) -> Unit,
    actionClicked: (ContentSectionId) -> Unit,
    bsvacTextStyle: BsvacTextStyle
) {
    scope.apply {
        Row(modifier = Modifier.weight(1f)) {
            sectionIds.forEach { contentSectionId ->
                val modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .contentSectionBorder()
                    .background(ContentSectionId.backgroundColor(contentSectionId,mainUiState))
                    .updateContentSectionClickable(contentSectionId, navigationClicked, actionClicked)

                ContentSection(
                    title = contentSectionId.title,
                    modifier = modifier,
                    bsvacTextStyle = bsvacTextStyle
                )
            }
        }
    }
}

@Composable
fun MiddleRow(
    scope: ColumnScope,
    mainUiState: MainUiState,
    navigationClicked: (ContentSectionId) -> Unit,
    actionClicked: (ContentSectionId) -> Unit,
    content: @Composable (BoxScope.() -> Unit),
    bsvacTextStyle: BsvacTextStyle,
    sectionIds: List<ContentSectionId>
) {
    scope.apply {
        Row(modifier = Modifier.weight(5f) .fillMaxSize()) {
            MiddleRowContentSectionColumn(columnScope = this@apply,
                mainUiState = mainUiState,
                navigationClicked = navigationClicked,
                actionClicked = actionClicked,
                bsvacTextStyle = bsvacTextStyle,
                sectionIds = sectionIds
            )
            Box(modifier = Modifier.weight(8f)
                .fillMaxSize()
                .background(Color.Black)
            ) {
                content()
            }
        }
    }
}

@Composable
fun MiddleRowContentSectionColumn(columnScope: ColumnScope,
                                  mainUiState: MainUiState,
                                  navigationClicked: (ContentSectionId) -> Unit,
                                  actionClicked: (ContentSectionId) -> Unit,
                                  bsvacTextStyle: BsvacTextStyle,
                                  sectionIds: List<ContentSectionId>
) {
    columnScope.apply {
        Column(modifier = Modifier.weight(1f)) {
            sectionIds
                .forEach { contentSectionId ->
                    val modifier = Modifier.weight(1f)
                        .contentSectionBorder()
                        .background(color = ContentSectionId.backgroundColor(contentSectionId,mainUiState))
                        .updateContentSectionClickable(contentSectionId, navigationClicked, actionClicked)

                    ContentSection(
                        contentSectionId.title,
                        modifier = modifier,
                        bsvacTextStyle = bsvacTextStyle
                    )
                }
        }
    }
}

@Composable
fun StatusRow(scope: ColumnScope,
              statusMessage: String,
              statusSymbol: String,
              dateString: String,
              bsvacTextStyle: BsvacTextStyle) {
    scope.apply {
        Row(modifier = Modifier.fillMaxWidth()
            .background(Color.Black)
        ) {
            Text(
                text = statusMessage,
                color = Color.White,
                fontSize = bsvacTextStyle.fontSize,
                modifier = Modifier.weight(12f)
                .contentSectionBorder()
            )
            Text(statusSymbol,
                color = Color.White,
                fontSize = bsvacTextStyle.fontSize,
                textAlign = TextAlign.Center,
                modifier = Modifier.weight(1f)
                    .background(Color(0xFF006F00))
                    .contentSectionBorder()
            )
            Text(
                text = dateString,
                color = Color.White,
                fontSize = bsvacTextStyle.fontSize,
                textAlign = TextAlign.Center,
                modifier = Modifier.weight(3f)
                    .background(Color(0xFF0000FF))
                    .contentSectionBorder()

            )
        }
    }
}

@Composable
fun BottomRow(
    scope: ColumnScope,
    mainUiState: MainUiState,
    navigationClicked: (ContentSectionId) -> Unit,
    actionClicked: (ContentSectionId) -> Unit,
    bsvacTextStyle: BsvacTextStyle,
    sectionIds: List<ContentSectionId>
) {
    scope.apply {
        Row(modifier = Modifier.weight(1f)) {
            sectionIds.forEach { contentSectionId ->
                val modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .contentSectionBorder()
                    .background(ContentSectionId.backgroundColor(contentSectionId,mainUiState))
                    .updateContentSectionClickable(contentSectionId, navigationClicked, actionClicked)

                ContentSection(
                    contentSectionId.title,
                    modifier = modifier,
                    bsvacTextStyle = bsvacTextStyle
                )
            }
        }
    }
}

private fun Modifier.updateContentSectionClickable(
    contentSectionId: ContentSectionId,
    navigationClicked: (ContentSectionId) -> Unit,
    actionClicked: (ContentSectionId) -> Unit
) : Modifier {
    val navigate = contentSectionId.isNavigatable
    val action = contentSectionId.isActionable
    if (navigate && action) {
        return this.then(Modifier.clickable{
            actionClicked(contentSectionId)
            navigationClicked(contentSectionId)
        })
    } else if (navigate) {
        return this.then(Modifier.clickable {
            navigationClicked(contentSectionId)
        })
    } else if (action) {
        return this.then(Modifier.clickable {
            actionClicked(contentSectionId)
        })
    } else {
        return this
    }
}
